import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Plus, Edit2, Trash2, Key, LogOut, ChevronLeft, ChevronRight,
  Crown, Shield, X, Eye, Ban, Check, Download, CheckSquare, Square,
  Clock, BookOpen, CreditCard, Award, UserCheck, Activity, ArrowLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = ['free', 'pro', 'premium'];
const PLAN_COLORS = { free: 'bg-gray-500/20 text-gray-300', pro: 'bg-blue-500/20 text-blue-300', premium: 'bg-amber-500/20 text-amber-300' };
const EVENT_ICONS = { account: UserCheck, lecture: BookOpen, study: Clock, payment: CreditCard, subscription: Crown, achievement: Award, admin_action: Shield };
const EVENT_COLORS = { account: 'text-emerald-400', lecture: 'text-blue-400', study: 'text-purple-400', payment: 'text-amber-400', subscription: 'text-cyan-400', achievement: 'text-pink-400', admin_action: 'text-red-400' };

// ─── Add/Edit User Modal ───
const UserModal = ({ user, onClose, onSaved }) => {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || '', email: user?.email || '', password: '', plan: user?.subscription_plan || 'free', is_admin: user?.is_admin || false, disabled: user?.disabled || false
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/users/${user.id}`, { name: form.name, email: form.email, disabled: form.disabled, is_admin: form.is_admin });
        if (form.plan !== (user.subscription_plan || 'free')) {
          await axios.post(`${API}/admin/users/${user.id}/change-subscription`, { plan: form.plan });
        }
        if (form.password) {
          await axios.post(`${API}/admin/users/${user.id}/reset-password`, { password: form.password });
        }
        toast.success('User updated');
      } else {
        await axios.post(`${API}/admin/users`, { name: form.name, email: form.email, password: form.password, plan: form.plan });
        toast.success('User created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#1a2236] border border-white/10  p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="user-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">{isEdit ? 'Edit User' : 'Add User'}</h3>
          <button aria-label="Close" onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg"><X className="w-5 h-5 text-white/60" /></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs text-white/50 mb-1 block">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="bg-white/5 border-white/10 text-white" data-testid="user-modal-name" /></div>
          <div><label className="text-xs text-white/50 mb-1 block">Email</label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com" type="email" className="bg-white/5 border-white/10 text-white" data-testid="user-modal-email" /></div>
          <div><label className="text-xs text-white/50 mb-1 block">{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label><Input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder={isEdit ? 'Leave blank' : 'Min 6 characters'} type="password" className="bg-white/5 border-white/10 text-white" data-testid="user-modal-password" /></div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Subscription Plan</label>
            <div className="flex gap-2">
              {PLANS.map(p => (<button key={p} onClick={() => setForm(prev => ({ ...prev, plan: p }))} className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${form.plan === p ? 'bg-[#002FA7] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`} data-testid={`user-modal-plan-${p}`}>{p}</button>))}
            </div>
          </div>
          <div className="flex gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.is_admin} onChange={e => setForm(p => ({ ...p, is_admin: e.target.checked }))} className="rounded" data-testid="user-modal-admin-toggle" /><Shield className="w-3.5 h-3.5" /> Admin</label>
            {isEdit && <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.disabled} onChange={e => setForm(p => ({ ...p, disabled: e.target.checked }))} className="rounded" data-testid="user-modal-disabled-toggle" /><Ban className="w-3.5 h-3.5" /> Disabled</label>}
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl border-white/10 text-white/70">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.email || (!isEdit && !form.password)} className="flex-1 rounded-xl bg-[#002FA7] hover:bg-[#002FA7]/80" data-testid="user-modal-save">{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Activity Timeline Panel ───
const ActivityTimeline = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/users/${userId}/activity`);
        setData(d);
      } catch { toast.error('Failed to load activity'); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div className="text-center py-12 text-white/40">Loading timeline...</div>;
  if (!data) return null;

  const { user: u, timeline, total_events } = data;
  const fmtTs = (ts) => ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="space-y-4" data-testid="activity-timeline">
      <div className="flex items-center gap-3">
        <button aria-label="Back" onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl text-white/60" data-testid="timeline-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h3 className="text-lg font-bold text-white">{u.name || u.email}</h3>
          <p className="text-sm text-white/40">{u.email} &middot; {total_events} events</p>
        </div>
        <span className={`ml-auto px-3 py-1 rounded-lg text-xs font-semibold capitalize ${PLAN_COLORS[u.subscription_plan] || PLAN_COLORS.free}`}>{u.subscription_plan || 'free'}</span>
      </div>

      {/* User summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3"><p className="text-xs text-white/40">Joined</p><p className="text-sm text-white font-semibold">{fmtTs(u.created_at)?.split(',')[0] || '—'}</p></div>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3"><p className="text-xs text-white/40">Last Login</p><p className="text-sm text-white font-semibold">{fmtTs(u.last_login)?.split(',')[0] || 'Never'}</p></div>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3"><p className="text-xs text-white/40">Auth</p><p className="text-sm text-white font-semibold capitalize">{u.auth_provider || 'email'}</p></div>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3"><p className="text-xs text-white/40">Status</p><p className={`text-sm font-semibold ${u.disabled ? 'text-red-400' : 'text-emerald-400'}`}>{u.disabled ? 'Disabled' : 'Active'}</p></div>
      </div>

      {/* Timeline */}
      <div className="relative pl-6 space-y-0">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-white/10" />
        {timeline.map((e, i) => {
          const Icon = EVENT_ICONS[e.type] || Activity;
          const color = EVENT_COLORS[e.type] || 'text-white/40';
          return (
            <div key={i} className="relative flex gap-3 py-2.5 group" data-testid={`timeline-event-${i}`}>
              <div className={`absolute -left-6 mt-0.5 w-[22px] h-[22px] rounded-full bg-[#1a2236] border border-white/10 flex items-center justify-center ${color}`}>
                <Icon className="w-3 h-3" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">{e.action}</p>
                {e.detail && <p className="text-xs text-white/40 truncate">{e.detail}</p>}
              </div>
              <span className="text-xs text-white/25 whitespace-nowrap mt-0.5">{fmtTs(e.timestamp)}</span>
            </div>
          );
        })}
        {timeline.length === 0 && <p className="text-white/30 text-sm py-4">No activity recorded yet.</p>}
      </div>
    </div>
  );
};

// ─── Main Users Tab ───
export const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkPlan, setBulkPlan] = useState('pro');
  const [activityUser, setActivityUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set('q', search);
      if (planFilter) params.set('plan', planFilter);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API}/admin/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => setSelected(prev => prev.size === users.length ? new Set() : new Set(users.map(u => u.id)));

  const handleBulkAction = async () => {
    const ids = [...selected];
    if (!ids.length) return toast.error('No users selected');
    try {
      if (bulkAction === 'subscription') {
        await axios.post(`${API}/admin/users/bulk/change-subscription`, { user_ids: ids, plan: bulkPlan });
        toast.success(`${ids.length} users changed to ${bulkPlan}`);
      } else if (bulkAction === 'disable') {
        await axios.post(`${API}/admin/users/bulk/toggle-status`, { user_ids: ids, disabled: true });
        toast.success(`${ids.length} users disabled`);
      } else if (bulkAction === 'enable') {
        await axios.post(`${API}/admin/users/bulk/toggle-status`, { user_ids: ids, disabled: false });
        toast.success(`${ids.length} users enabled`);
      } else if (bulkAction === 'delete') {
        await axios.post(`${API}/admin/users/bulk/delete`, { user_ids: ids });
        toast.success(`${ids.length} users deleted`);
      }
      setSelected(new Set());
      setBulkAction('');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (planFilter) params.set('plan', planFilter);
    if (statusFilter) params.set('status', statusFilter);
    const token = localStorage.getItem('auth_token');
    window.open(`${API}/admin/users/export/csv?${params}&token=${token}`, '_blank');
    // Fallback: fetch and download
    axios.get(`${API}/admin/users/export/csv?${params}`, { responseType: 'blob' }).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'users.csv'; a.click();
    }).catch(() => toast.error('Export failed'));
  };

  const handleImpersonate = async (u) => {
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/impersonate`);
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('impersonating', 'true');
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      toast.success(`Now viewing as ${u.email}`);
      window.location.href = '/app';
    } catch { toast.error('Failed to impersonate'); }
  };

  const handleForceLogout = async (u) => {
    try { await axios.post(`${API}/admin/users/${u.id}/force-logout`); toast.success(`Sessions cleared for ${u.email}`); } catch { toast.error('Failed'); }
  };

  const handleDelete = async (u) => {
    try { await axios.delete(`${API}/admin/users/${u.id}`); toast.success(`Deleted ${u.email}`); setConfirmDelete(null); fetchUsers(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleQuickPlan = async (u, plan) => {
    try { await axios.post(`${API}/admin/users/${u.id}/change-subscription`, { plan }); toast.success(`${u.email} → ${plan}`); fetchUsers(); } catch { toast.error('Failed'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // Show activity timeline if a user is selected
  if (activityUser) {
    return <ActivityTimeline userId={activityUser.id} onClose={() => setActivityUser(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      {/* Search, Filters, Actions */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or email..." className="pl-10 bg-white/5 border-white/10 text-white rounded-xl" data-testid="users-search" />
          </div>
          <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }} className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm" data-testid="users-plan-filter">
            <option value="">All Plans</option>
            {PLANS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2 text-sm" data-testid="users-status-filter">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          <Button onClick={handleExportCSV} variant="outline" className="rounded-xl border-white/10 text-white/70 gap-2" data-testid="export-csv-btn"><Download className="w-4 h-4" /> CSV</Button>
          <Button onClick={() => setModal('add')} className="rounded-xl bg-[#002FA7] hover:bg-[#002FA7]/80 gap-2" data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add User</Button>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#002FA7]/10 border border-[#002FA7]/20" data-testid="bulk-action-bar">
            <span className="text-sm text-[#002FA7] font-semibold">{selected.size} selected</span>
            <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1 text-sm" data-testid="bulk-action-select">
              <option value="">Choose action...</option>
              <option value="subscription">Change Plan</option>
              <option value="enable">Enable</option>
              <option value="disable">Disable</option>
              <option value="delete">Delete</option>
            </select>
            {bulkAction === 'subscription' && (
              <select value={bulkPlan} onChange={e => setBulkPlan(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-lg px-2 py-1 text-sm capitalize" data-testid="bulk-plan-select">
                {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <Button size="sm" onClick={handleBulkAction} disabled={!bulkAction} className="rounded-lg bg-[#002FA7] text-xs px-3" data-testid="bulk-apply-btn">Apply</Button>
            <button onClick={() => { setSelected(new Set()); setBulkAction(''); }} className="ml-auto text-xs text-white/40 hover:text-white">Clear</button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm text-white/50">
        <span>{total} users</span>
        <span>Page {page}/{pages}</span>
      </div>

      {/* Users Table */}
      <div className=" border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-white/10 text-left text-white/40">
              <th className="px-3 py-3 w-10"><button onClick={toggleAll} className="text-white/40 hover:text-white" data-testid="select-all-btn">{selected.size === users.length && users.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}</button></th>
              <th className="px-3 py-3 font-medium">User</th>
              <th className="px-3 py-3 font-medium">Plan</th>
              <th className="px-3 py-3 font-medium hidden md:table-cell">Role</th>
              <th className="px-3 py-3 font-medium hidden lg:table-cell">Joined</th>
              <th className="px-3 py-3 font-medium hidden lg:table-cell">Status</th>
              <th className="px-3 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-white/40">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-white/40">No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className={`border-b border-white/5 hover:bg-white/[0.03] transition-colors ${selected.has(u.id) ? 'bg-[#002FA7]/5' : ''}`} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-3"><button onClick={() => toggleSelect(u.id)} className="text-white/40 hover:text-white">{selected.has(u.id) ? <CheckSquare className="w-4 h-4 text-[#002FA7]" /> : <Square className="w-4 h-4" />}</button></td>
                <td className="px-3 py-3">
                  <button onClick={() => setActivityUser(u)} className="text-left hover:underline underline-offset-2" data-testid={`user-name-${u.id}`}>
                    <div className="font-medium text-white truncate max-w-[200px]">{u.name || '—'}</div>
                    <div className="text-xs text-white/40 truncate max-w-[200px]">{u.email}</div>
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="relative group">
                    <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-semibold capitalize cursor-pointer ${PLAN_COLORS[u.subscription_plan] || PLAN_COLORS.free}`}>{u.subscription_plan === 'premium' && <Crown className="w-3 h-3 inline mr-1" />}{u.subscription_plan || 'free'}</span>
                    <div className="absolute z-20 top-full left-0 mt-1 hidden group-hover:flex flex-col bg-[#1a2236] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                      {PLANS.map(p => (<button key={p} onClick={() => handleQuickPlan(u, p)} className={`px-4 py-1.5 text-xs text-left capitalize hover:bg-white/10 ${(u.subscription_plan || 'free') === p ? 'text-[#002FA7] font-bold' : 'text-white/70'}`}>{p === 'premium' && '★ '}{p}</button>))}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 hidden md:table-cell">{u.is_admin ? <span className="text-amber-400 text-xs font-semibold flex items-center gap-1"><Shield className="w-3 h-3" /> Admin</span> : <span className="text-white/30 text-xs">User</span>}</td>
                <td className="px-3 py-3 text-white/40 text-xs hidden lg:table-cell">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-3 hidden lg:table-cell">{u.disabled ? <span className="text-red-400 text-xs flex items-center gap-1"><Ban className="w-3 h-3" /> Disabled</span> : <span className="text-emerald-400 text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Active</span>}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setActivityUser(u)} title="Activity" className="p-1.5 hover:bg-purple-500/20 rounded-lg text-white/40 hover:text-purple-400 transition-colors" data-testid={`activity-${u.id}`}><Activity className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setModal(u)} title="Edit" className="p-1.5 hover:bg-white/10 rounded-lg text-white/40 hover:text-white transition-colors" data-testid={`edit-user-${u.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleImpersonate(u)} title="Impersonate" className="p-1.5 hover:bg-blue-500/20 rounded-lg text-white/40 hover:text-blue-400 transition-colors" data-testid={`impersonate-${u.id}`}><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleForceLogout(u)} title="Force Logout" className="p-1.5 hover:bg-orange-500/20 rounded-lg text-white/40 hover:text-orange-400 transition-colors" data-testid={`force-logout-${u.id}`}><LogOut className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setConfirmDelete(u)} title="Delete" className="p-1.5 hover:bg-red-500/20 rounded-lg text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl border-white/10 text-white/60" data-testid="users-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
          {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
            const p = pages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= pages - 3 ? pages - 6 + i : page - 3 + i;
            if (p < 1 || p > pages) return null;
            return <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(p)} className={`rounded-xl min-w-[36px] ${p === page ? 'bg-[#002FA7]' : 'border-white/10 text-white/60'}`}>{p}</Button>;
          })}
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-xl border-white/10 text-white/60" data-testid="users-next-page"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {modal && <UserModal user={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchUsers(); }} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}>
          <div className="bg-[#1a2236] border border-red-500/20  p-6 w-full max-w-sm mx-4 text-center" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <Trash2 className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">Delete User</h3>
            <p className="text-sm text-white/50 mb-5">Permanently delete <strong className="text-white">{confirmDelete.email}</strong>?</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} className="flex-1 rounded-xl border-white/10 text-white/70">Cancel</Button>
              <Button onClick={() => handleDelete(confirmDelete)} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
